import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const InsightsPage = () => {
  const { locale } = useParams();
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = ['All', 'Pharma Engineering'];

  const articles = [
    {
      slug: 'gmp-facility-design-considerations',
      title: 'Key Considerations in Designing cGMP-Compliant Pharma Facilities',
      category: 'Pharma Engineering',
      excerpt: 'Designing a pharma facility is not just construction — it is compliance, efficiency, and long-term operability. A poorly designed facility leads to regulatory observations, operational inefficiencies, and costly modifications.',
      readTime: '6 min read',
      image: '/images/external/photo-1559757148-5c350d0d3c56.jpg',
    },
    {
      slug: 'why-pmc-critical-pharma-projects',
      title: 'Why Project Management Consultancy (PMC) is Critical for Pharma Projects',
      category: 'Pharma Engineering',
      excerpt: 'Pharma projects are highly complex — HVAC, utilities, cleanrooms, compliance, and validation must all converge. Without proper coordination, projects suffer delays, cost overruns, and compliance gaps. This is where PMC plays a crucial role.',
      readTime: '5 min read',
      image: '/images/external/photo-1486406146926-c627a92ad1ab.jpg',
    },
    {
      slug: 'pharma-hvac-design-mistakes',
      title: 'Common Mistakes in Pharma HVAC Design (And How to Avoid Them)',
      category: 'Pharma Engineering',
      excerpt: 'HVAC systems are the backbone of any pharmaceutical facility. From pressure cascades to contamination control, HVAC design directly impacts product quality and audit outcomes. Here are the mistakes we see most often — and how to avoid them.',
      readTime: '7 min read',
      image: '/images/external/photo-1581092580497-e0d23cbdf1dc.jpg',
    },
    {
      slug: 'why-pharma-projects-delayed',
      title: 'Why Pharma Projects Get Delayed (And How to Prevent It)',
      category: 'Pharma Engineering',
      excerpt: 'Pharma project delays are rarely caused by one big failure — they are the cumulative result of small, ignored issues at design, vendor, and execution stages. Here is how to identify and prevent them early.',
      readTime: '6 min read',
      image: '/images/external/photo-1587854692152-cbe660dbde88.jpg',
    },
    {
      slug: 'cleanroom-design-mistakes',
      title: 'Common Mistakes in Cleanroom Design',
      category: 'Pharma Engineering',
      excerpt: 'Cleanroom design is unforgiving — small errors at concept stage cascade into compliance failures, contamination risks, and operational nightmares. These are the recurring mistakes we see in field audits.',
      readTime: '6 min read',
      image: '/images/external/photo-1532187863486-abf9dbad1b69.jpg',
    },
    {
      slug: 'utility-systems-fail-pharma',
      title: 'Why Utility Systems Fail in Pharma Plants',
      category: 'Pharma Engineering',
      excerpt: 'Compressed air, water, steam, and HVAC utilities are the silent backbone of every pharma plant. When they fail, production halts and compliance suffers. The failure modes are predictable — and preventable.',
      readTime: '7 min read',
      image: '/images/external/photo-1518770660439-4636190af475.jpg',
    },
    {
      slug: 'top-7-gmp-facility-mistakes',
      title: 'Top 7 Mistakes in cGMP Facility Planning',
      category: 'Pharma Engineering',
      excerpt: 'Most cGMP facility projects start with great intent but stumble on the same seven mistakes — layout, classification, utilities, documentation, automation, change control, and validation planning.',
      readTime: '7 min read',
      image: '/images/external/photo-1576091160399-112ba8d25d1d.jpg',
    },
    {
      slug: 'pressure-cascade-cleanrooms-guide',
      title: 'Understanding Pressure Cascade in Cleanrooms (A Simple Practical Guide)',
      category: 'Pharma Engineering',
      excerpt: 'Pressure cascade is the most misunderstood part of cleanroom HVAC. This is the practical, field-tested approach we use across audits and design reviews — no jargon, no theory, just what works.',
      readTime: '6 min read',
      image: '/images/external/photo-1559757148-5c350d0d3c56.jpg',
    },
    {
      slug: 'compressed-air-system-pharma',
      title: 'How to Design a Reliable Compressed Air System for Pharma',
      category: 'Pharma Engineering',
      excerpt: 'Compressed air is a critical cGMP utility — but it is the most under-engineered system on the plant. From compressor sizing to ISO 8573 quality classes, here is the design playbook that works.',
      readTime: '7 min read',
      image: '/images/external/photo-1565793298595-6a879b1d9492.jpg',
    },
    {
      slug: 'pw-water-system-design',
      title: 'PW Water System Design Basics for Pharma Engineers',
      category: 'Pharma Engineering',
      excerpt: 'Purified Water (PW) is the most-used cGMP utility — and one of the most-cited in audit observations. A clear primer on loop design, sanitization strategy, distribution, and validation.',
      readTime: '7 min read',
      image: '/images/external/photo-1582719471384-894fbb16e074.jpg',
    },
    {
      slug: 'select-right-pharma-consultant',
      title: 'How to Select the Right Pharma Consultant or PMC',
      category: 'Pharma Engineering',
      excerpt: 'Selecting the right consultant or PMC partner is the single highest-leverage decision in your project. Brand names matter less than execution depth — here is the evaluation framework we recommend.',
      readTime: '6 min read',
      image: '/images/external/photo-1554224155-8d04cb21cd6c.jpg',
    },
    {
      slug: 'pmc-vs-epc-pharma',
      title: 'PMC vs EPC — What Should Pharma Companies Choose?',
      category: 'Pharma Engineering',
      excerpt: 'PMC vs EPC is not a one-size-fits-all choice. The right model depends on project complexity, internal team strength, risk appetite, and timeline. A side-by-side comparison for decision-makers.',
      readTime: '7 min read',
      image: '/images/external/photo-1565793298595-6a879b1d9492.jpg',
    },
    {
      slug: 'lessons-25-years-pharma-engineering',
      title: 'Lessons Learned from 25 Years in Pharma Engineering',
      category: 'Pharma Engineering',
      excerpt: 'After 25 years across HVAC, utilities, cleanrooms, and PMC, certain patterns repeat across every project. These are the lessons we wish every promoter, plant head, and project sponsor knew at the start.',
      readTime: '8 min read',
      image: '/images/external/photo-1518770660439-4636190af475.jpg',
    },
  ];

  const filteredArticles = activeFilter === 'All' 
    ? articles 
    : articles.filter(a => a.category === activeFilter);

  return (
    <div className="pt-20" data-testid="insights-page">
      <SEO 
        title="Insights & Thought Leadership"
        description="Expert analysis and strategic thinking from H&G teams on pharma engineering, ESG sustainability, capital markets, M&A, and finance."
        path="/insights"
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-teal font-medium text-sm uppercase tracking-wider">Thought Leadership</span>
            <h1 className="font-playfair text-4xl md:text-5xl text-navy mt-4 mb-4">
              Insights & Perspectives
            </h1>
            <p className="text-lg text-mid-grey">
              Expert analysis and strategic thinking from our teams across all five practice areas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`filter-tab px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === filter 
                    ? 'active bg-teal text-white' 
                    : 'bg-light-grey text-navy hover:bg-gray-200'
                }`}
                data-testid={`filter-${filter.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            key={activeFilter}
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredArticles.map((article) => (
              <motion.article
                key={article.slug}
                variants={fadeInUp}
                className="insight-card bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
                data-testid={`article-card-${article.slug}`}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-teal bg-teal/10 px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                    <span className="text-xs text-mid-grey flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-playfair text-lg text-navy mb-3 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-mid-grey text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <Link 
                    to={`/${locale}/insights/${article.slug}`}
                    className="inline-flex items-center text-teal font-medium text-sm hover:text-teal transition-colors"
                  >
                    Read More <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {filteredArticles.length === 0 && (
            <div className="text-center text-mid-grey py-12">
              No articles in this category yet. Check back soon.
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon section removed — all blogs now published */}
    </div>
  );
};

export default InsightsPage;
